import { useState, useEffect, useCallback } from 'react';
import { profileApi } from '../services/profile-api';
import type { UserProfile, UpdateProfileData, AvatarUploadResponse } from '../types';

interface UseProfileReturn {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<UserProfile>;
  uploadAvatar: (file: File) => Promise<AvatarUploadResponse>;
}

export function useProfile(accessToken: string | null): UseProfileReturn {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await profileApi.getProfile(accessToken);
      setProfile(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(
    async (data: UpdateProfileData): Promise<UserProfile> => {
      if (!accessToken) throw new Error('Not authenticated');
      const updated = await profileApi.updateProfile(accessToken, data);
      setProfile(updated);
      return updated;
    },
    [accessToken]
  );

  const uploadAvatar = useCallback(
    async (file: File): Promise<AvatarUploadResponse> => {
      if (!accessToken) throw new Error('Not authenticated');
      const result = await profileApi.uploadAvatar(accessToken, file);
      setProfile((prev) => (prev ? { ...prev, avatarUrl: result.avatarUrl } : prev));
      return result;
    },
    [accessToken]
  );

  return {
    profile,
    isLoading,
    error,
    refetch: fetchProfile,
    updateProfile,
    uploadAvatar,
  };
}
